import { observer } from 'mobx-react'
import { useContext, useState } from 'react'
import type { ReactNode } from 'react'
import { Button, LoadingSpinner } from '@/components/Generic'
import type { Base64File } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { cx } from '@/utils/cx'
import type {
  DocumentRequest,
  FileUploadScreen as FileUploadScreenType,
  UploadedDocument,
} from '@/types/dataCollection'
import { ScreenHeader } from './ScreenHeader'
import { UploadSlot } from './UploadSlot'
import { documentsForRequest, resolveLabel, slotMinFiles, validateUploads } from './helpers'
import { HelpSection } from './HelpSection'

interface FileUploadScreenProps {
  screen: FileUploadScreenType
  uploadedDocuments: UploadedDocument[]
  /** Rendered under the screen header, e.g. the retry notice. */
  notice?: ReactNode
  submitting: boolean
  canGoBack: boolean
  onBack: () => void
  onUpload: (request: DocumentRequest, file: Base64File) => Promise<void>
  onDelete: (documentId: string) => Promise<void>
  onSubmit: () => void
}

export const FileUploadScreen = observer(
  ({
    screen,
    notice,
    uploadedDocuments,
    submitting,
    canGoBack,
    onBack,
    onUpload,
    onDelete,
    onSubmit,
  }: FileUploadScreenProps) => {
    const store = useContext(StoreContext)
    const { isMobile } = store.AppState
    const { language } = store.TranslationsState
    const { dataCollectionPage: trans } = store.TranslationsState.translations

    const { notice: uploadNotice, requirements, documentRequests } = screen.fileUpload
    const [slotErrors, setSlotErrors] = useState<Record<string, string>>({})
    const [uploadingSlots, setUploadingSlots] = useState<Set<string>>(new Set())
    const anyUploading = uploadingSlots.size > 0

    const setSlotUploading = (requestId: string, uploading: boolean) => {
      setUploadingSlots((prev) => {
        const next = new Set(prev)
        if (uploading) next.add(requestId)
        else next.delete(requestId)
        return next
      })
    }

    const handlePrimary = () => {
      const errors = validateUploads(
        documentRequests,
        uploadedDocuments,
        trans.upload.requiredError
      )
      if (Object.keys(errors).length > 0) {
        setSlotErrors(errors)
        return
      }
      onSubmit()
    }

    const anyRequired = documentRequests.some((r) => slotMinFiles(r) > 0)
    const primaryLabel = uploadedDocuments.length > 0 || anyRequired ? trans.next : trans.skip
    const uploadsValid = documentRequests.every(
      (r) => documentsForRequest(uploadedDocuments, r.id).length >= slotMinFiles(r)
    )

    return (
      <div className="page-container mobile-m-0">
        <div className="layout__content-container-with-scroll">
          <ScreenHeader title={screen.title} description={screen.description} icon={screen.icon} />
          {notice}

          {uploadNotice && (
            <div className="data-collection__upload-notice">
              <span className="data-collection__upload-notice-title">
                {resolveLabel(uploadNotice.title, language)}
              </span>
              <p className="data-collection__upload-notice-text">
                {resolveLabel(uploadNotice.text, language)}
              </p>
            </div>
          )}

          {requirements && requirements.items.length > 0 && (
            <div className="data-collection__section">
              <span className="data-collection__section-title data-collection__requirements-title">
                {resolveLabel(requirements.title, language)}
              </span>
              <ol className="data-collection__requirements" role="list">
                {requirements.items.map((item, index) => (
                  <li className="data-collection__requirement" key={item.id}>
                    <span className="data-collection__message-number">{index + 1}</span>
                    <span className="data-collection__requirement-text">
                      <span className="data-collection__requirement-label">
                        {resolveLabel(item.label, language)}
                      </span>
                      {item.description && (
                        <span className="data-collection__requirement-description">
                          {resolveLabel(item.description, language)}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {documentRequests.map((request) => {
            const documents = documentsForRequest(uploadedDocuments, request.id)
            const requiredError =
              slotErrors[request.id] && documents.length < slotMinFiles(request)
                ? slotErrors[request.id]
                : undefined
            return (
              <UploadSlot
                key={request.id}
                request={request}
                documents={documents}
                requiredError={requiredError}
                onUpload={onUpload}
                onDelete={onDelete}
                onUploadingChange={(uploading) => setSlotUploading(request.id, uploading)}
              />
            )
          })}

          {screen.help && <HelpSection help={screen.help} />}
        </div>

        <div className="layout__content-container-bottom-fixed">
          {canGoBack ? (
            <Button
              onClick={onBack}
              disabled={submitting || anyUploading}
              className="button--secondary data-collection__back"
              dataTestid="data-collection-back"
            >
              {trans.back}
            </Button>
          ) : (
            !isMobile && <div className="layout__content-container-bottom-left" />
          )}
          <Button
            onClick={handlePrimary}
            disabled={submitting || anyUploading || !uploadsValid}
            dataTestid="data-collection-next"
          >
            {submitting && <LoadingSpinner inButton />}
            <span
              className={cx('generic__button-text', submitting && 'generic__button-text--loading')}
            >
              {primaryLabel}
            </span>
          </Button>
        </div>
        <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
      </div>
    )
  }
)
